// ============================================
// APPLICATION - Dashboard Hooks
// ============================================

import { useQuery } from '@tanstack/react-query';
import { dashboardRepository } from '../../infrastructure/http/dashboard.repository';
import { queryKeys } from '../query-keys';
import type { DashboardFiltros } from '../../domain/dtos';
import type { DashboardKpis } from '../../domain/entities';
import { useAuthStore } from '../../state/auth.store';

/**
 * Hook para obtener resumen del dashboard
 */
export const useDashboardSummary = (filtros?: DashboardFiltros) => {
  return useQuery({
    queryKey: queryKeys.dashboard.summary(filtros),
    queryFn: () => dashboardRepository.getSummary(filtros),
    staleTime: 2 * 60 * 1000, // 2 minutos
    refetchInterval: 5 * 60 * 1000, // Refetch cada 5 minutos
  });
};

/**
 * Hook para obtener KPIs del dashboard
 * Solo se ejecuta si hay token y el usuario pasó los gates de sesión
 */
export const useDashboardKpis = () => {
  const { session, accessToken } = useAuthStore();

  // Solo habilitado si hay token y no hay gates bloqueantes
  const isEnabled = !!(
    accessToken &&
    session &&
    !session.mustAnswerSurvey &&
    (session.hasFreemiumAccess || session.hasPremiumAccess)
  );

  return useQuery<DashboardKpis>({
    queryKey: ['dashboard', 'kpis'],
    queryFn: () => dashboardRepository.getKpis(),
    enabled: isEnabled,
    staleTime: 10 * 30 * 1000, // 10 minutos
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    // refetchInterval: false, // desactiva auto-refresh
    refetchInterval: 20 * 60 * 1000, // Refetch cada 20 minutos
    retry: 2,
  });
};

/**
 * Hook para gráfico de ventas por período
 */
export const useVentasPorPeriodo = (params: {
  fechaInicio: string;
  fechaFin: string;
  agrupacion: 'dia' | 'semana' | 'mes';
}) => {
  return useQuery({
    queryKey: queryKeys.dashboard.ventasPeriodo(params),
    queryFn: () => dashboardRepository.getVentasPorPeriodo(params),
    enabled: Boolean(params.fechaInicio && params.fechaFin),
    staleTime: 5 * 60 * 1000,
  });
};
